import { z } from "zod";

import {
  VibeAppEdgeReachabilityReadSchema,
  VibeAppHealthCheckConfigReadSchema,
  VibeAppIconSchema,
  VibeAppResourceQuotasSchema,
  VibeAppVisibilityReadSchema
} from "../../../../schemas/VibeApp";
import {
  VIBE_BUILD_COMPUTE_SIZE_DEFAULT,
  VibeBuildComputeSizeReadSchema
} from "../../../../shared/domain/vibe/build-compute-size";
import {
  VIBE_SHIP_GATE_MODE_DEFAULT,
  VibeShipGateModeReadSchema
} from "../../../../shared/domain/vibe/ship-gate-mode";

/**
 * Shared identity grammar for the org-scoped Vibe resources that carry a
 * human-readable name: apps and git projects. Names are org-unique and
 * serve as CLI handles (`nexus app deploy my-handler`), so the validators
 * enforce a shape that round-trips cleanly between CLI argv, URL segments,
 * S3 prefixes, and Forgejo repo names.
 *   - lowercase letters, digits, hyphen
 *   - must start with a letter
 *   - 1–63 chars (DNS label cap — apps eventually back subdomains)
 *
 * Apps and git projects MUST share this grammar rather than each declaring
 * their own: provisioning a project for an app copies the app's name
 * verbatim as the project (and therefore Forgejo repo) name, so every legal
 * app name has to be a legal project name. Two independent grammars could
 * drift and make that copy produce an unaddressable project.
 */
const VibeResourceNameSchema = z
  .string()
  .trim()
  .min(1, "name is required")
  .max(63, "name must be ≤ 63 characters")
  .regex(
    /^[a-z][a-z0-9-]*$/,
    "name must start with a lowercase letter and contain only lowercase letters, digits, and hyphens"
  );

const VibeResourceDescriptionSchema = z
  .string()
  .trim()
  .max(500, "description must be ≤ 500 characters");

export const VibeAppNameSchema = VibeResourceNameSchema;

export const VibeAppDescriptionSchema = VibeResourceDescriptionSchema;

/**
 * VibeGitProject identity. Doubles as the Forgejo repo name, stamped at
 * creation and stable for the project's lifetime.
 */
export const VibeGitProjectNameSchema = VibeResourceNameSchema;

export const VibeGitProjectDescriptionSchema = VibeResourceDescriptionSchema;

/**
 * The git branch whose pushes trigger a deployment, stored on the app as a
 * plain branch name (e.g. `main`, `release/prod`). The push-ingest policy
 * composes `refs/heads/<branch>` from this before comparing against the
 * pushed ref — see `composeVibeDeployRef`.
 *
 * A conservative git-ref-name subset: starts with a letter or digit; only
 * letters, digits, `.`, `_`, `-`, `/`; no `..`, no trailing `/`/`.`, no
 * `.lock` suffix. ≤ 255 chars. Bounds the input without mirroring git's
 * full ref-name grammar — the platform only ever composes a `refs/heads/`
 * ref from it.
 */
export const VibeDeployBranchSchema = z
  .string()
  .trim()
  .min(1, "deploy branch is required")
  .max(255, "deploy branch must be ≤ 255 characters")
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
    "deploy branch must start with a letter or digit and contain only letters, digits, '.', '_', '-', and '/'"
  )
  .refine((b) => !b.includes(".."), "deploy branch must not contain '..'")
  .refine((b) => !b.endsWith("/") && !b.endsWith("."), "deploy branch must not end with '/' or '.'")
  .refine((b) => !b.endsWith(".lock"), "deploy branch must not end with '.lock'");

export type VibeDeployBranch = z.infer<typeof VibeDeployBranchSchema>;

/**
 * A git project's default branch — the branch the Forgejo repo is
 * materialized with. Shares the branch grammar with `VibeDeployBranchSchema`
 * for the same reason apps and projects share a name grammar: provisioning a
 * project for an app copies the app's `deployBranch` verbatim into the
 * project's `defaultBranch`, so every legal deploy branch must be a legal
 * default branch. The two are independent at runtime (N apps may watch N
 * branches of one project) but their grammars must not drift.
 */
export const VibeGitProjectDefaultBranchSchema = VibeDeployBranchSchema;

/**
 * The default deploy branch for a fresh app. Kept in sync with the
 * `VibeApp.deployBranch` column DEFAULT in schema.prisma — `VibeApp.create()`
 * stamps this, and the DB backfilled existing rows with the same literal.
 */
export const VIBE_DEFAULT_DEPLOY_BRANCH = "main";

/**
 * The default branch for a fresh git project. Kept in sync with the
 * `VibeGitProject.defaultBranch` column DEFAULT in schema.prisma.
 */
export const VIBE_DEFAULT_GIT_PROJECT_BRANCH = "main";

/**
 * Compose the fully-qualified ref a deploy branch corresponds to:
 * `main` → `refs/heads/main`. The ingest policy compares a pushed ref
 * against this, so a tag push (`refs/tags/...`) never matches a deploy
 * branch — branches deploy, tags do not.
 */
export function composeVibeDeployRef(branch: string): string {
  return `refs/heads/${branch}`;
}

/**
 * Read shape for a VibeApp returned to the client. Keep it slim — extra
 * fields land as the platform grows (currentDeploymentId, repoUrl,
 * processTypes, etc.) and are additive for consumers.
 */
export const VibeAppSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  name: VibeAppNameSchema,
  description: VibeAppDescriptionSchema.nullable(),
  /// When true, prod deploys land in AWAITING_APPROVAL until an
  /// approval request resolves (next slice). Opt-in per app, default
  /// false — ungated is the natural state for first-party / staging.
  requireApprovals: z.boolean(),
  /// How hard the server-side ship gate applies to this app's deploys — `OFF`,
  /// `WARN` or `ENFORCE`. Opt-in per app, default `OFF`, exactly like
  /// `requireApprovals`: nothing about an app's contents turns it on by itself.
  ///
  /// The artifact set and each artifact's predicate live in
  /// `VIBE_SHIP_GATE_ARTIFACTS`; this only says whether they are consulted and
  /// what a finding costs.
  ///
  /// `.default("OFF")`, not a bare required enum, for the same deploy-skew
  /// reason as `icon` below: a backend one release behind the console omits the
  /// key entirely, and a required key turns that window into a whole-response
  /// parse failure — an org with apps is told it has none. Absent reads as
  /// UNGATED, which is both the safe rendering and what every backend that has
  /// never heard of this field actually means. It is only a display default:
  /// the gate is applied server-side off the app's real column, so a console
  /// showing `OFF` during the skew cannot wave a deploy through.
  shipGateMode: VibeShipGateModeReadSchema.default(VIBE_SHIP_GATE_MODE_DEFAULT),
  /// `shipGateMode === "ENFORCE"`, projected for clients that predate the mode.
  ///
  /// DERIVED, never stored — the column behind it was replaced by the mode, and
  /// the presenter computes this from the same value it puts in `shipGateMode`
  /// so the two cannot disagree. Kept because a published CLI reads it off this
  /// view and renders it in a table; dropping the key would silently blank that
  /// column for anyone who has not upgraded.
  ///
  /// `WARN` projects to `false`, which is the honest answer to the only question
  /// a boolean can ask: a warning app does not refuse. A client that needs to
  /// tell `WARN` from `OFF` has to read `shipGateMode`.
  requireVerification: z.boolean().default(false),
  /// The git branch whose pushes trigger a deployment (plain branch name,
  /// e.g. `main`). Default seeded by `VibeApp.create()`; changeable per-app
  /// via the PATCH endpoint. The ingest policy composes `refs/heads/<branch>`.
  deployBranch: VibeDeployBranchSchema,
  /// Per-app Nomad jobspec limits (cpuMhz / memoryMiB / maxInstances).
  /// Defaults seeded by `VibeApp.create()`; admins raise these per-app
  /// via the PATCH endpoint when the build runner emits OOM/throttle
  /// signals.
  resourceQuotas: VibeAppResourceQuotasSchema,
  /// The compute this app's image builds on — `MEDIUM` (7 GB) or `LARGE`
  /// (15 GB), separate from the runtime `resourceQuotas`. Bites on the CodeBuild
  /// executor only. Changeable per-app via the PATCH endpoint; a change reaches
  /// the next build admitted, never one already in flight.
  ///
  /// `.default(LARGE)` for the deploy-skew reason `shipGateMode` states: a
  /// backend one release behind the console omits the key, and LARGE is what
  /// every build ran on before the setting existed. Display only — admission
  /// reads the app's real column.
  buildComputeSize: VibeBuildComputeSizeReadSchema.default(VIBE_BUILD_COMPUTE_SIZE_DEFAULT),
  /// Per-app health-check policy (path / port / timeouts / thresholds).
  /// Defaults seeded by `VibeApp.create()`; admins tune it per-app via
  /// the PATCH endpoint (dedicated `/healthz`, longer boot grace, etc.).
  healthCheckConfig: VibeAppHealthCheckConfigReadSchema,
  /// Canonical public URL `https://<app>.<tenant>.<base-domain>`, stamped
  /// at creation and stable for the app's lifetime. Nullable only for
  /// transition-era rows that predate the column.
  publicUrl: z.string().url().nullable(),
  /// The AgentExternalTool this app is registered as, or `null` when it is not
  /// registered. Set once via the register-as-tool bridge and cleared when the
  /// app (and its linked tool) is deleted. Gates re-registration in the console.
  linkedToolId: z.string().uuid().nullable(),
  /// Whether the app is reachable by anyone with the URL (`PUBLIC`) or only via
  /// its edge-auth header / agent tool calls (`PRIVATE`). A STORED intent chosen
  /// at create time (default `PRIVATE`), NOT derived from the token's presence —
  /// so a lost/corrupt token can never silently re-label a PRIVATE app as public
  /// (the deploy path fails closed instead). Changeable for the app's lifetime
  /// via `PATCH /api/vibe/apps/:id/visibility`, which moves the intent and the
  /// token together.
  visibility: VibeAppVisibilityReadSchema,
  /// What the tenant's own edge last answered when the agent's probe asked
  /// whether this app's public host is actually routed, and when it answered.
  ///
  /// **`null` means NEVER OBSERVED, not "fine".** The probe only asks about a
  /// deployment that is healthy and has settled, so most apps carry null most of
  /// the time. A UI that renders null as a green tick re-creates the exact
  /// silently-green failure the probe exists to end.
  ///
  /// Orthogonal to the deployment's status rather than a replacement for it: an
  /// `UNROUTED` app's latest deployment is still HEALTHY, and showing both at
  /// once is the point — the workload is fine and the routing is not.
  edgeReachability: VibeAppEdgeReachabilityReadSchema.nullable(),
  edgeReachabilityAt: z.coerce.date().nullable(),
  /// The edge's own short reason for that verdict, or the probe's transport
  /// fault. Diagnostics for a human; the verdict is the enum above.
  edgeReachabilityDetail: z.string().nullable(),
  /// The app's stored icon, or `null` when it has none — in which case the
  /// console renders the monogram generated from the app's name, so an app is
  /// distinguishable with nothing uploaded and nothing stored.
  ///
  /// One nullable object rather than a `url` and a `source` beside each other:
  /// the database pairs the underlying columns with a CHECK constraint, and
  /// grouping them here makes the half-set state unrepresentable in the
  /// contract too. `url` is resolved from the stored object key server-side —
  /// the key is a bucket-layout detail and never crosses this boundary.
  ///
  /// `nullish`, not `nullable`: a backend one deploy behind the console omits
  /// the key entirely, and a required key turns that skew into a whole-response
  /// parse failure — an org with apps is told it has none. Absent and `null`
  /// mean the same thing here, so accept both.
  icon: VibeAppIconSchema.nullish(),
  createdByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeAppDto = z.infer<typeof VibeAppSchema>;
